// AdminBrandingPage.jsx — master admin per-domain branding editor.
//
// Wraps the tenant-facing `TenantBrandingPage` with a domain picker so
// master admins can jump between tenants without leaving the page. All
// backend calls flow through the same `/api/tenant/branding*` endpoints
// with a `?domain_id=<uuid>` override — no duplicated code, no separate
// admin-only field set.
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Palette, Loader2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import TenantBrandingPage from '../portal/TenantBrandingPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBrandingPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [domains, setDomains] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/domains`, { headers });
        if (cancelled) return;
        const list = (r.data?.domains || r.data || [])
          .filter((d) => d.status !== 'inactive')
          .map((d) => ({
            id: d.id,
            name: (d.company?.name) || d.yabbit_domain || d.slug || d.id,
            accent: d.custom_color || '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setDomains(list);
        // Auto-select the first tenant so the editor renders immediately.
        if (list.length && !selected) setSelected(list[0].id);
      } catch (_e) {
        // No-op — the "no domains" empty state below handles this.
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [headers, selected]);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-4" data-testid="admin-branding-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Palette className="w-6 h-6 text-teal-400" />
              Branding
            </h1>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              Set the accent colour, logo, icon, company name, and typography for any tenant on the platform. The same field set is available to every domain admin from their tenant portal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-white/50 font-medium uppercase tracking-widest">Tenant</label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-400 min-w-[220px]"
              data-testid="admin-branding-tenant-picker"
            >
              {domains.length === 0 && <option value="">No domains available</option>}
              {domains.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
          </div>
        ) : !selected ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center text-white/50 text-sm">
            No tenants configured yet. Add a domain from the sidebar first.
          </div>
        ) : (
          // The tenant editor renders on a WHITE surface — wrap it in a
          // padded card so it sits comfortably on the dark admin console.
          <div className="bg-white rounded-xl overflow-hidden">
            <TenantBrandingPage
              key={selected}
              domainIdOverride={selected}
              embedded
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBrandingPage;
